import { RefObject } from 'react'
import styled, { css } from 'styled-components/macro'
import { respondAbove, respondBelow } from 'styles/mediaQueries'
import { scrollbar } from 'styles/mixins'
import Box from 'components/shared/Box'
import {
    fadeInOnRender,
    fadeInSequence,
    slideInSequence,
} from 'styles/animations'

const PADDING_XL = '32px'
const MOBILE_MODAL_HEADER_HEIGHT = '56px'

interface WrapperProps {
    isMobileFullscreen: boolean
    isCenteredVerticaly: boolean
    isBlur?: true
    isMobileCentered?: true
    ref?: RefObject<HTMLDivElement>
}
// TODO: organize z-index layers into constants

export const Wrapper = styled.div<WrapperProps>`
    ${scrollbar}
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    z-index: 99999;
    justify-content: center;
    align-items: ${({ isCenteredVerticaly }) =>
        isCenteredVerticaly ? 'center' : 'flex-start'};
    width: 100vw;
    min-height: calc(100 * var(--vh));
    background-color: ${({ theme }) => theme.colors.grayscale_900_transparent};
    transition: min-height 100ms linear;
    display: flex;
    overflow: auto;
    padding: 16px;
    margin-top: 0 !important;
    ${fadeInOnRender};

    ${({ isBlur }) =>
        isBlur &&
        css`
            backdrop-filter: blur(6px);
        `}

    @media ${respondBelow.md} {
        align-items: ${({ isMobileCentered }) =>
            isMobileCentered ? 'center' : 'flex-start'};
    }

    ${({ isMobileFullscreen }) =>
        isMobileFullscreen
            ? css`
                  min-height: calc(
                      100 * var(--vh) - ${MOBILE_MODAL_HEADER_HEIGHT}
                  );
                  @media ${respondBelow.md} {
                      bottom: 0;
                      padding: 0;

                      ${Inner} {
                          border-radius: 0;
                          min-height: 100%;
                          width: 100%;
                          display: flex;
                          flex-direction: column;
                          background-color: ${({ theme }) =>
                              theme.colors.grayscale_200};
                      }

                      ${Close} {
                          top: 16px;
                          right: 16px;
                      }
                  }
              `
            : css`
                  @media ${respondBelow.md} {
                      bottom: auto;
                  }
              `}

    @media ${respondAbove.xl} {
        padding: ${PADDING_XL};
    }
`

interface InnerProps {
    maxWidth: number
    isMobileFullscreen?: boolean
    isCenteredVerticaly: boolean
    isBlur?: true
}

export const Inner = styled.div<InnerProps>`
    ${scrollbar}
    position: relative;
    background-color: ${({ theme, isBlur }) =>
        isBlur ? 'transparent' : theme.colors.grayscale_0};
    transition: min-height 100ms linear;
    max-width: 100%;
    width: 100%;
    border-radius: 8px;

    ${({ isMobileFullscreen }) =>
        !isMobileFullscreen &&
        css`
            animation: ${slideInSequence} 0.4s ease-in-out,
                ${fadeInSequence} 0.4s ease-in-out;
        `}

    @media ${respondAbove.md} {
        height: fit-content;
        max-height: calc(100 * var(--vh) - 2 * ${PADDING_XL});
        overflow-x: hidden;
        overflow-y: auto;
        max-width: ${({ maxWidth }) => `${maxWidth}px`};

        ${({ isMobileFullscreen }) =>
            isMobileFullscreen &&
            css`
                animation: ${slideInSequence} 0.4s ease-in-out,
                    ${fadeInSequence} 0.4s ease-in-out;
            `}
    }
    @media ${respondAbove.lg} {
        margin-top: ${({ isCenteredVerticaly }) =>
            isCenteredVerticaly ? '0' : '100px'};
    }
    @media ${respondAbove.xl} {
        background-color: ${({ theme }) => theme.colors.grayscale_0};
        border-radius: 8px;
    }
`

interface GlobalModalHeaderProps {
    isMobileFullscreen?: boolean
}

export const GlobalModalHeader = styled(Box)<GlobalModalHeaderProps>`
    position: sticky;
    top: 0;
    z-index: 1000;
    background-color: ${({ theme }) => theme.colors.grayscale_0};
    border-radius: ${({ isMobileFullscreen }) =>
        isMobileFullscreen ? 0 : '8px 8px 0 0'};
    box-shadow: 0 7px 20px 0 rgba(0, 0, 0, 0.07);

    @media ${respondAbove.md} {
        border-radius: 8px 8px 0 0;
        box-shadow: none;
    }
`

export const Close = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
    z-index: 99;

    @media ${respondAbove.xl} {
        top: 32px;
        right: 32px;
    }

    & > span {
        transition: color 100ms ease-in;

        &:hover {
            @media ${respondAbove.xl} {
                color: ${({ theme }) => theme.colors.primary_700};
            }
        }
    }

    &:focus-visible {
        span {
            outline: 2px solid ${({ theme }) => theme.colors.primary_500};
            border-radius: 4px;
        }
    }
`
